import { ZodError } from "zod";

import type { TEnvSchema } from "./env.schema";
import { envSchema } from "./env.schema";

let cachedEnv: TEnvSchema | null = null;

const validateEnv = (): TEnvSchema => {
  if (cachedEnv) {
    return cachedEnv;
  }

  try {
    cachedEnv = envSchema.parse(import.meta.env ?? process.env);
    return cachedEnv;
  } catch (error) {
    if (error instanceof ZodError) {
      const issues = error.issues
        .map((issue) => `${issue.path.join(".")}: ${issue.message}`)
        .join("; ");
      throw new Error(
        `Environment validation failed. Please check your Vite variables. Details: ${issues}`,
      );
    }

    throw error;
  }
};

export const getEnvVar = <TEnvKey extends keyof TEnvSchema>(
  key: TEnvKey,
): TEnvSchema[TEnvKey] => {
  const env = validateEnv();
  return env[key];
};

export const getEnv = (): TEnvSchema => {
  return validateEnv();
};

export const isDevMode = (): boolean => {
  return getEnvVar("MODE") === "development";
};

export const isProdMode = (): boolean => {
  return getEnvVar("MODE") === "production";
};

export const isOnPremise = (): boolean => {
  return getEnvVar("VITE_ON_PREMISE");
};

export const isOplotFlavor = (): boolean => {
  return getEnvVar("VITE_PLATFORM_FLAVOR") === "oplot";
};

// NOTE: NSAI-6427 — the three spellings that mean "this is the NodeShift build".
// Not two: CI builds the web app with `VITE_PLATFORM_FLAVOR=default`
// (`validation.yml`), the dev bundle and the Playwright mock use `nodeshift`, and
// a plain build sets nothing at all. This list is the client-side mirror of the
// shell mapping in `.github/workflows/build-ui.yml` and `deploy.yml`, which spell
// the same rule as `= "default" || = "nodeshift" || -z`; a flavor added to one
// belongs in the other.
const NODESHIFT_FLAVORS = ["nodeshift", "default"];

// NOTE: NSAI-6427 — the NodeShift build, named rather than assumed. `isOplotFlavor`
// answers "is this the one other flavor we ship", which is the wrong question for
// anything that renders the NodeShift mark itself: under it, every flavor that is
// not Oplot — including one added tomorrow for a white-label customer — counts as
// NodeShift and gets NodeShift's artwork.
//
// Any value outside the list above is somebody else's product, which is the same
// direction the deploy scripts take (an unrecognized flavor becomes that flavor's
// image prefix). The cost of that strictness is real and was paid once: a flavor
// this list does not know silently drops NodeShift's own mark, which is how the
// missing `default` showed up — as every empty-chat spec failing in the
// production E2E leg while passing against the dev bundle. It is still the right
// direction: the other one puts NodeShift's mark inside a customer's product.
export const isNodeShiftFlavor = (): boolean => {
  const flavor = getEnvVar("VITE_PLATFORM_FLAVOR")?.trim().toLowerCase();

  return !flavor || NODESHIFT_FLAVORS.includes(flavor);
};
